import React, { useEffect, useRef, useState } from 'react';
import { ConfigProvider, Button, Input, Spin, message } from 'antd';
import { QueryClientProvider } from 'react-query';
import { useStore } from './store/store';
import { queryClient } from './queryClient';
import { deleteScratchTemplate } from './requests/templates';
import { initializeBoxContent, reinitializeBoxWidthAndHeight } from './utils/boxes';
import { computeColumnWidth } from './utils/columns';
import { marginsObject } from './constants/gridConfig';
import { defaultItemValues, ItemTypes, MASKS_TYPE, uuid } from './constants/constants';
import { getDocumentFromParams, getUrlParamsObject } from './utils/browser';
import { mutateMakeCompoundWithSingleSubShape } from './components/Shape/shapeUtils';
import ShapeFullscreenEditor from './components/Shape/ShapeFullscreenEditor';
import { hydrateShapeTexts, serializeShapeTexts } from './components/Shape/shapeText';
import { convertFromRaw, EditorState } from 'draft-js';
import { decorator } from './components/RichEditor/utils';
import './proposalApp.css';
import './costingEmbed.css';

// Vue « composer une forme » standalone : une seule box Forme, en plein écran,
// sans le reste de l'éditeur (sidebar, toolbar, container...). Destinée à être
// embarquée en iframe dans le costing (admin → Banque de formes), via
// EDITOR_URL?jwt=<jwt>&view=shape-scratch — même mécanique que LibraryApp,
// mais il n'y a ici aucun document réel à ouvrir : au montage, cette vue crée
// elle-même un template jetable (`scratch: true`, cf.
// Api::Proposals::TemplatesController#destroy_scratch) avec une unique box,
// le temps de la composition. « Terminer » envoie la forme composée au
// parent par postMessage (c'est LUI qui l'enregistre dans la banque — avec sa
// propre policy admin-editor/superadmin, cf. Admin::BankShapesController) et
// détruit ce template pour de bon ; il n'a plus aucune raison d'exister.
const postToParent = (type, payload = {}) => {
  try {
    window.parent.postMessage({ source: 'proposal-shape-scratch', type, ...payload }, '*');
  } catch (e) {
    // parent inaccessible (ouvert hors iframe) : sans effet.
  }
};

const selector = (state) => ({
  createTemplateInStore: state.createTemplateInStore,
  postTemplate: state.postTemplate,
  addContainers: state.addContainers,
  boxes: state.boxes,
});

// Mode édition (cf. ShapeBank/index.jsx#composerSrc) : la forme existante à
// modifier n'a pas de route de lecture dédiée à ajouter côté back — la banque
// admin l'a déjà en mémoire (venue du GET dossiers), donc elle voyage
// directement dans l'URL (JSON base64, query param `shape`) plutôt que par un
// handshake postMessage une fois l'iframe chargée : ce dernier s'est révélé
// intermittent en pratique (rien ne garantit que l'écouteur du parent soit
// déjà attaché quand l'enfant annonce être prêt), source d'un chargement
// bloqué indéfiniment.
const editShapeFromUrl = () => {
  const encoded = new URLSearchParams(window.location.search).get('shape');
  if (!encoded) return null;
  try {
    // `encoded` est déjà décodé une fois par URLSearchParams — c'est le
    // base64 brut posé côté banque admin (cf. ShapeBank/index.jsx#composerSrc).
    return JSON.parse(decodeURIComponent(escape(atob(encoded))));
  } catch (e) {
    return null;
  }
};

const ShapeScratchApp = () => {
  const { createTemplateInStore, postTemplate, addContainers, boxes } = useStore(selector);
  const [templateId, setTemplateId] = useState(null);
  const [boxId, setBoxId] = useState(null);
  const [shapeName, setShapeName] = useState('');
  const [status, setStatus] = useState('loading'); // loading | ready | done
  const editorRef = useRef(null);
  const cleanedUpRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      createTemplateInStore('opportunity');
      const state = useStore.getState();
      let newBox = initializeBoxContent({
        box: { type: ItemTypes.SHAPE, ...defaultItemValues.shape, copied: false },
        configuration: state.configuration,
        titles: [],
      });
      const margins = marginsObject(state.configuration);
      newBox = reinitializeBoxWidthAndHeight({
        box: newBox,
        columnWidth: computeColumnWidth({
          landscape: false,
          marginLeft: margins.left,
          marginRight: margins.right,
        }),
        onCreate: true,
        header: state.masks.find((m) => m.id === MASKS_TYPE.HEADER.id),
        footer: state.masks.find((m) => m.id === MASKS_TYPE.FOOTER.id),
      });
      addContainers({ box: newBox });
      const created = useStore.getState().boxes[0];
      if (!created) {
        message.error('Impossible de préparer la forme.');
        return;
      }
      // ShapeFullscreenEditor n'est normalement ouvert que depuis une box déjà
      // compound (cf. mutateMakeCompoundWithSingleSubShape) — sans quoi son
      // panneau de réglages (câblé sur isCompound=true sans le revérifier)
      // reste vide malgré une forme bien visible sur le canevas.
      useStore.getState().updateBox(created.id, (draft) => mutateMakeCompoundWithSingleSubShape(draft, uuid()));
      const editShape = editShapeFromUrl();
      if (editShape) {
        useStore.getState().updateBox(created.id, (draft) => {
          draft.width = editShape.width;
          draft.height = editShape.height;
          draft.content.shapes = hydrateShapeTexts(
            editShape.definition,
            (raw) => EditorState.createWithContent(convertFromRaw(raw), decorator)
          );
        });
      }
      await postTemplate({ name: 'Composition de forme (temporaire)', templatable: 'opportunity', scratch: true });
      if (cancelled) return;
      // postTemplate ne peuple que `currentTemplate`, jamais `currentDocument`
      // (réservé à l'app principale, cf. store.js/api.js) — or c'est ce que lit
      // ShapeBankPicker (la "Bibliothèque de formes" du panneau) pour charger
      // les dossiers. `postTemplate` met déjà `tid` à jour dans l'URL : il
      // suffit de la relire, comme au démarrage normal de l'app (cf. store.js).
      useStore.setState({ currentDocument: getDocumentFromParams(getUrlParamsObject()) });
      setTemplateId(useStore.getState().currentTemplate?.id ?? null);
      setBoxId(created.id);
      if (editShape) setShapeName(editShape.name ?? '');
      setStatus('ready');
    };
    bootstrap();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanup = async () => {
    if (cleanedUpRef.current || !templateId) return;
    cleanedUpRef.current = true;
    await deleteScratchTemplate(templateId);
  };

  const handleCancel = async () => {
    postToParent('cancel');
    await cleanup();
    setStatus('done');
  };

  const handleFinish = async () => {
    const box = useStore.getState().boxes.find((b) => b.id === boxId);
    const shapes = box?.content?.shapes;
    if (!shapeName.trim() || !shapes?.length) {
      message.error('Composez au moins deux formes et donnez un nom avant de terminer.');
      return;
    }
    const dataUrl = editorRef.current?.generateThumbnail();
    const thumbnailContent = dataUrl?.includes(',') ? dataUrl.split(',')[1] : undefined;
    postToParent('shape-composed', {
      name: shapeName.trim(),
      width: box.width,
      height: box.height,
      // Les textes partent en JSON brut : un EditorState ne passe pas postMessage.
      definition: serializeShapeTexts(shapes),
      thumbnailContent,
    });
    await cleanup();
    setStatus('done');
  };

  if (status === 'loading') {
    return (
      <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size='large' />
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'rgba(11,34,57,0.6)' }}>Vous pouvez fermer cette fenêtre.</span>
      </div>
    );
  }

  const box = boxes.find((b) => b.id === boxId);
  if (!box) return null;

  return (
    <ConfigProvider>
      <QueryClientProvider client={queryClient}>
        <ShapeFullscreenEditor
          ref={editorRef}
          box={box}
          open
          fullscreen
          sidePanelExtra={(
            <div
              data-outside-ignore='true'
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}
            >
              <Input
                placeholder='Nom de la forme'
                value={shapeName}
                onChange={(e) => setShapeName(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <Button onClick={handleCancel} style={{ flex: 1 }}>Annuler</Button>
                <Button type='primary' onClick={handleFinish} disabled={!shapeName.trim()} style={{ flex: 1 }}>
                  Terminer
                </Button>
              </div>
            </div>
          )}
        />
        {/* Hôte de la barre de mise en forme et du menu de variables d'un
            texte de forme (cf. BoxDraftText), comme dans Layout. */}
        <div id='SelectionVariablePortal' />
      </QueryClientProvider>
    </ConfigProvider>
  );
};

export default ShapeScratchApp;
